import {
  FileTextOutlined,
  GiftOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
} from '@ant-design/icons';

// Admin main menu: each group is a sidebar submenu, and its tabs are the submenu items.
// Every tab is still its own route in routes.js (or a ?tab= of one) - the menu only
// navigates, so URLs and deep links are unchanged.
//
// `match` lists extra path prefixes (detail/edit pages) that belong to a tab, so the
// right group and tab stay highlighted on e.g. /order/:id. `tab` is for pages that
// switch their content by a ?tab= query param instead of separate routes.
//
// `permissions` lists what a tab needs (all of them); tabs the signed-in user lacks are
// hidden by PermissionsContext. Keep them in step with the route's `permissions` in routes.js.
const SCOPE = 'scope:all';
export const menuGroups = [
  {
    key: 'korisnici',
    icon: <TeamOutlined />,
    label: 'Korisnici',
    // One page (korisnici.js) switched by ?tab=, so these tabs share a path.
    tabs: [
      {
        key: 'users',
        permissions: ['users:read', SCOPE],
        label: 'Korisnici',
        path: '/korisnici',
        tab: 'users',
        match: ['/korisnik'],
      },
      {
        key: 'admins',
        permissions: ['users:read', 'users:staff:read', SCOPE],
        label: 'Admini',
        path: '/korisnici',
        tab: 'admins',
      },
      {
        key: 'mentori',
        permissions: ['network:manage', SCOPE],
        label: 'Mentori',
        path: '/korisnici',
        tab: 'mentori',
        match: ['/medics'],
      },
      {
        key: 'organizacije',
        permissions: ['network:manage', SCOPE],
        label: 'Organizacije',
        path: '/korisnici',
        tab: 'organizacije',
        match: ['/clinic'],
      },
      {
        key: 'agenti',
        permissions: ['network:manage', SCOPE],
        label: 'Agenti',
        path: '/korisnici',
        tab: 'agenti',
        match: ['/agenti'],
      },
      {
        key: 'pending',
        permissions: ['users:read', SCOPE],
        label: 'Na čekanju',
        path: '/korisnici',
        tab: 'pending',
      },
    ],
  },
  {
    key: 'webshop',
    icon: <ShopOutlined />,
    label: 'Webshop',
    tabs: [
      {
        key: 'proizvodi',
        permissions: ['catalog:manage'],
        label: 'Proizvodi',
        path: '/proizvodi',
      },
      {
        key: 'narudzbine',
        permissions: ['commerce:orders:read', SCOPE],
        label: 'Narudžbine',
        path: '/narudzbine',
        match: ['/order/'],
      },
      {
        key: 'pretplate',
        permissions: ['commerce:subscriptions:manage', SCOPE],
        label: 'Pretplate',
        path: '/pretplatnicki-sistem',
      },
    ],
  },
  {
    key: 'lojalnost',
    icon: <GiftOutlined />,
    label: 'Lojalnost',
    tabs: [
      {
        key: 'poeni',
        permissions: ['loyalty:manage'],
        label: 'Poeni i gamifikacija',
        path: '/points-and-gamification',
      },
      {
        key: 'vip',
        permissions: ['users:write', SCOPE],
        label: 'VIP članovi',
        path: '/vips',
        match: ['/vip/'],
      },
      {
        key: 'kvizovi',
        permissions: ['content:manage'],
        label: 'Kvizovi',
        path: '/kvizovi',
      },
      {
        key: 'nagrada',
        permissions: ['reports:read', SCOPE],
        label: 'Nagrada',
        path: '/nagrada',
      },
      {
        key: 'lojalnost-podesavanja',
        permissions: ['loyalty:manage'],
        label: 'Podešavanja',
        path: '/lojalnost-podesavanja',
      },
    ],
  },
  {
    key: 'sadrzaj',
    icon: <FileTextOutlined />,
    label: 'Sadržaj',
    tabs: [
      {
        key: 'objave',
        permissions: ['content:manage'],
        label: 'Objave',
        path: '/objave',
      },
      {
        key: 'notifikacije',
        permissions: ['content:manage'],
        label: 'Notifikacije',
        path: '/notifikacije',
      },
      {
        key: 'protokoli',
        permissions: ['catalog:manage'],
        label: 'Protokoli',
        path: '/protokoli',
      },
      {
        key: 'mobilni-info',
        permissions: ['content:manage'],
        label: 'Mobilni info',
        path: '/mobilni-info',
      },
    ],
  },
  {
    key: 'podesavanja',
    icon: <SettingOutlined />,
    label: 'Podešavanja',
    tabs: [
      {
        key: 'role',
        permissions: ['roles:read'],
        label: 'Role i permisije',
        path: '/role-i-permisije',
      },
      {
        key: 'ai-pravila',
        permissions: ['ai:outreach:read'],
        label: 'AI pravila',
        path: '/ai-pravila',
      },
      {
        key: 'istorija-akcija',
        permissions: ['audit:read'],
        label: 'Istorija akcija',
        path: '/istorija-akcija',
      },
    ],
  },
];

const matchesPrefix = (pathname, prefix) =>
  pathname === prefix ||
  pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`);

export const tabUrl = (tab) =>
  tab.tab ? `${tab.path}?tab=${tab.tab}` : tab.path;

// Returns { group, tab } for the current location, or null when the page is not in the
// menu. Tabs sharing a path are told apart by ?tab=, falling back to the first of them.
export const findActiveMenu = (pathname, search = '') => {
  const queryTab = new URLSearchParams(search).get('tab');

  for (const group of menuGroups) {
    const matching = group.tabs.filter((tab) =>
      [tab.path, ...(tab.match || [])].some((prefix) =>
        matchesPrefix(pathname, prefix)
      )
    );
    if (matching.length === 0) continue;

    const byQuery = matching.find((tab) => tab.tab && tab.tab === queryTab);
    return { group, tab: byQuery || matching[0] };
  }
  return null;
};
